import {
  EWebAnalyticsProvider,
  EWebAnalyticsSearchType,
  EWebAnalyticsSortDirection,
  EWebAnalyticsTableKind,
  type IWebAnalyticsFilter,
  type IWebAnalyticsQueryPageRow,
  type IWebAnalyticsTableBusiness,
  type IWebAnalyticsTableRow,
  type IWebAnalyticsTableSettings,
  type TWebAnalyticsFilterName,
  type TWebAnalyticsTableMetric,
} from '@mahawi/eshop-common';
import {
  Button,
  Drawer,
  Input,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnType } from 'antd/es/table';
import {
  type FilterValue,
  type SorterResult,
  type SortOrder,
  type TableCurrentDataSource,
} from 'antd/es/table/interface';
import React from 'react';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import {
  type IWebAnalyticsQueryPagesRequest,
  type IWebAnalyticsQueryPagesResponse,
  type IWebAnalyticsTableResponse,
} from '../../reducers/web-analytics-insights/types';
import { FONT_SIZE, SPACING } from '../../theme/tokens';
import Link from '../link';
import Table from '../table';
import { formatChangePct, formatMetric, getChangeTone } from './format';
import GapNote from './gap-note';

const { Text } = Typography;

export const KIND_OPTIONS: { value: EWebAnalyticsTableKind; label: string }[] =
  [
    { value: EWebAnalyticsTableKind.PAGES, label: 'Pages' },
    { value: EWebAnalyticsTableKind.QUERIES, label: 'Queries' },
    { value: EWebAnalyticsTableKind.PRODUCTS, label: 'Products' },
    { value: EWebAnalyticsTableKind.COUNTRIES, label: 'Countries' },
    { value: EWebAnalyticsTableKind.DEVICES, label: 'Devices' },
    {
      value: EWebAnalyticsTableKind.SEARCH_APPEARANCE,
      label: 'Search appearance',
    },
    { value: EWebAnalyticsTableKind.CHANNELS, label: 'Channels' },
    { value: EWebAnalyticsTableKind.SOURCE_MEDIUM, label: 'Source / medium' },
    { value: EWebAnalyticsTableKind.LANDING_PAGES, label: 'Landing pages' },
  ];

export const GA4_TABLE_KINDS: EWebAnalyticsTableKind[] = [
  EWebAnalyticsTableKind.CHANNELS,
  EWebAnalyticsTableKind.SOURCE_MEDIUM,
  EWebAnalyticsTableKind.LANDING_PAGES,
];

export const BUSINESS_TABLE_KINDS: EWebAnalyticsTableKind[] = [
  EWebAnalyticsTableKind.PAGES,
  EWebAnalyticsTableKind.PRODUCTS,
  EWebAnalyticsTableKind.LANDING_PAGES,
];

export const PROVIDER_OPTIONS: {
  value: EWebAnalyticsProvider;
  label: string;
}[] = [
  { value: EWebAnalyticsProvider.GOOGLE, label: 'Google Search Console' },
  { value: EWebAnalyticsProvider.BING, label: 'Bing' },
  { value: EWebAnalyticsProvider.SEZNAM, label: 'Seznam' },
  { value: EWebAnalyticsProvider.CLOUDFLARE, label: 'Cloudflare' },
  { value: EWebAnalyticsProvider.GOOGLE_ANALYTICS, label: 'GA4 (sample)' },
];

export const SEARCH_TYPE_OPTIONS: {
  value: EWebAnalyticsSearchType;
  label: string;
}[] = Object.values(EWebAnalyticsSearchType)
  .filter(
    (searchType: EWebAnalyticsSearchType): boolean =>
      searchType !== EWebAnalyticsSearchType.NONE,
  )
  .map((searchType: EWebAnalyticsSearchType) => ({
    value: searchType,
    label: searchType,
  }));

export const DEVICE_OPTIONS: { value: string; label: string }[] = [
  { value: 'DESKTOP', label: 'Desktop' },
  { value: 'MOBILE', label: 'Mobile' },
  { value: 'TABLET', label: 'Tablet' },
];

const METRIC_TITLES: Record<TWebAnalyticsTableMetric, string> = {
  clicks: 'Clicks',
  impressions: 'Impressions',
  ctr: 'CTR',
  position: 'Position',
  sessions: 'Sessions',
  engagedSessions: 'Engaged sessions',
  engagementRate: 'Engagement rate',
  averageEngagementSeconds: 'Avg. engagement time',
  keyEvents: 'Key events',
  pageViews: 'Page views',
  users: 'Users',
};

const SEARCH_METRICS: TWebAnalyticsTableMetric[] = [
  'clicks',
  'impressions',
  'ctr',
  'position',
];

const GA4_METRICS: TWebAnalyticsTableMetric[] = [
  'sessions',
  'engagedSessions',
  'engagementRate',
  'averageEngagementSeconds',
  'keyEvents',
  'pageViews',
  'users',
];

const CLOUDFLARE_METRICS: TWebAnalyticsTableMetric[] = ['pageViews', 'users'];

export const isGa4Kind = (kind: EWebAnalyticsTableKind): boolean =>
  GA4_TABLE_KINDS.includes(kind);

export const isSampleSource = (
  kind: EWebAnalyticsTableKind,
  provider: EWebAnalyticsProvider | undefined,
): boolean =>
  isGa4Kind(kind) || provider === EWebAnalyticsProvider.GOOGLE_ANALYTICS;

export const SAMPLE_TAG_LABEL = 'sample (consented visitors only)';

export function SampleTag(): React.ReactElement {
  return <Tag color="gold">{SAMPLE_TAG_LABEL}</Tag>;
}

export const getTableMetrics = (
  kind: EWebAnalyticsTableKind,
  provider: EWebAnalyticsProvider | undefined,
): TWebAnalyticsTableMetric[] => {
  if (isSampleSource(kind, provider)) {
    return GA4_METRICS;
  }

  if (provider === EWebAnalyticsProvider.CLOUDFLARE) {
    return CLOUDFLARE_METRICS;
  }

  return SEARCH_METRICS;
};

export const getFilterRestrictions = (
  kind: EWebAnalyticsTableKind,
  filter: IWebAnalyticsFilter,
): Partial<Record<TWebAnalyticsFilterName, string>> => {
  const restrictions: Partial<Record<TWebAnalyticsFilterName, string>> = {};

  if (isGa4Kind(kind)) {
    restrictions.provider = 'Traffic tables always use the GA4 sample';
    restrictions.searchType = 'Search type applies to Google Search only';
  } else if (
    filter.provider !== undefined &&
    filter.provider !== EWebAnalyticsProvider.GOOGLE
  ) {
    restrictions.searchType = 'Search type applies to Google Search only';
  }

  if (kind === EWebAnalyticsTableKind.COUNTRIES) {
    restrictions.country = 'This table is already broken down by country';
  }

  if (kind === EWebAnalyticsTableKind.DEVICES) {
    restrictions.device = 'This table is already broken down by device';
  } else if (filter.country !== undefined) {
    restrictions.device = 'Country and device cannot be combined';
  }

  return restrictions;
};

export const toSortOrder = (
  settings: IWebAnalyticsTableSettings,
  sortBy: string,
): SortOrder =>
  settings.sortBy === sortBy
    ? settings.sortDir === EWebAnalyticsSortDirection.ASC
      ? 'ascend'
      : 'descend'
    : null;

export const toSortSettings = (
  sorter: SorterResult<object> | SorterResult<object>[],
): { sortBy: string; sortDir: EWebAnalyticsSortDirection } | null => {
  const [first] = Array.isArray(sorter) ? sorter : [sorter];

  if (!first?.order || typeof first.columnKey !== 'string') {
    return null;
  }

  return {
    sortBy: first.columnKey,
    sortDir:
      first.order === 'ascend'
        ? EWebAnalyticsSortDirection.ASC
        : EWebAnalyticsSortDirection.DESC,
  };
};

export const getDefaultSort = (
  kind: EWebAnalyticsTableKind,
  provider: EWebAnalyticsProvider | undefined,
): { sortBy: string; sortDir: EWebAnalyticsSortDirection } => ({
  sortBy: getTableMetrics(kind, provider)[0],
  sortDir: EWebAnalyticsSortDirection.DESC,
});

export const toDrillDownRequest = (
  kind: EWebAnalyticsTableKind,
  row: IWebAnalyticsTableRow,
): IWebAnalyticsQueryPagesRequest | null => {
  if (kind === EWebAnalyticsTableKind.PAGES) {
    return { page: row.key };
  }

  if (kind === EWebAnalyticsTableKind.QUERIES) {
    return { query: row.key };
  }

  if (kind === EWebAnalyticsTableKind.PRODUCTS) {
    return row.productUUID ? { productUUID: row.productUUID } : null;
  }

  return null;
};

export const canDrillDown = (
  kind: EWebAnalyticsTableKind,
  provider: EWebAnalyticsProvider | undefined,
): boolean =>
  !isGa4Kind(kind) &&
  (provider === undefined || provider === EWebAnalyticsProvider.GOOGLE);

export function EntityKey({
  row,
}: {
  row: IWebAnalyticsTableRow;
}): React.ReactElement {
  if (row.productUUID) {
    return (
      <Link to={`/product/${row.productUUID}`} label={row.key} openInNewTab />
    );
  }

  if (row.categoryUUID) {
    return (
      <Link to={`/category/${row.categoryUUID}`} label={row.key} openInNewTab />
    );
  }

  return <Text>{row.key}</Text>;
}

export function MetricCell({
  metric,
  row,
}: {
  metric: TWebAnalyticsTableMetric;
  row: IWebAnalyticsTableRow;
}): React.ReactElement {
  const changePct = row.changePct[metric];

  return (
    <Space orientation="vertical" size={SPACING.none}>
      <Text>{formatMetric(metric, row.current[metric])}</Text>
      {row.compare ? (
        <Text
          type={getChangeTone(metric, changePct)}
          style={{ fontSize: FONT_SIZE.sm }}
        >
          {formatChangePct(changePct)}
        </Text>
      ) : null}
    </Space>
  );
}

const renderBusiness =
  (field: keyof IWebAnalyticsTableBusiness) =>
  (_: string, row: IWebAnalyticsTableRow): string =>
    row.business ? formatMetric(field, row.business[field]) : EMPTY_VALUE;

export const buildColumns = ({
  kind,
  provider,
  settings,
  onDrillDown,
}: {
  kind: EWebAnalyticsTableKind;
  provider: EWebAnalyticsProvider | undefined;
  settings: IWebAnalyticsTableSettings;
  onDrillDown: (request: IWebAnalyticsQueryPagesRequest) => void;
}): ColumnType<IWebAnalyticsTableRow>[] => {
  const keyTitle =
    KIND_OPTIONS.find(({ value }) => value === kind)?.label ?? 'Key';
  const columns: ColumnType<IWebAnalyticsTableRow>[] = [
    {
      title: keyTitle,
      key: 'key',
      sorter: true,
      sortOrder: toSortOrder(settings, 'key'),
      render: (_: string, row: IWebAnalyticsTableRow): React.ReactElement => (
        <Space size={SPACING.sm}>
          <EntityKey row={row} />
          {row.isSample ? <SampleTag /> : null}
        </Space>
      ),
    },
    ...getTableMetrics(kind, provider).map(
      (
        metric: TWebAnalyticsTableMetric,
      ): ColumnType<IWebAnalyticsTableRow> => ({
        title: isSampleSource(kind, provider) ? (
          <Space size={SPACING.xs}>
            {METRIC_TITLES[metric]}
            <SampleTag />
          </Space>
        ) : (
          METRIC_TITLES[metric]
        ),
        key: metric,
        align: 'right',
        sorter: true,
        sortOrder: toSortOrder(settings, metric),
        render: (_: string, row: IWebAnalyticsTableRow): React.ReactElement => (
          <MetricCell metric={metric} row={row} />
        ),
      }),
    ),
  ];

  if (BUSINESS_TABLE_KINDS.includes(kind)) {
    columns.push(
      {
        title: 'Units sold',
        key: 'units',
        align: 'right',
        render: renderBusiness('units'),
      },
      {
        title: 'Revenue',
        key: 'revenueEur',
        align: 'right',
        render: renderBusiness('revenueEur'),
      },
      {
        title: (
          <Space size={SPACING.xs}>
            Margin
            <Tag color="gold">estimate</Tag>
          </Space>
        ),
        key: 'estimatedMarginEur',
        align: 'right',
        render: renderBusiness('estimatedMarginEur'),
      },
      {
        title: 'Stock',
        key: 'stock',
        align: 'right',
        render: renderBusiness('stock'),
      },
    );
  }

  if (canDrillDown(kind, provider)) {
    columns.push({
      title: 'Queries × pages',
      key: 'drillDown',
      render: (
        _: string,
        row: IWebAnalyticsTableRow,
      ): React.ReactElement | null => {
        const request = toDrillDownRequest(kind, row);

        return request ? (
          <Button
            size="small"
            type="link"
            onClick={(): void => {
              onDrillDown(request);
            }}
          >
            Detail
          </Button>
        ) : null;
      },
    });
  }

  return columns;
};

function RestrictedFilter({
  reason,
  children,
}: {
  reason: string | undefined;
  children: React.ReactElement;
}): React.ReactElement {
  return reason ? <Tooltip title={reason}>{children}</Tooltip> : children;
}

export function FilterBar({
  filter,
  kind,
  disabled,
  onChange,
}: {
  filter: IWebAnalyticsFilter;
  kind: EWebAnalyticsTableKind;
  disabled: boolean;
  onChange: (filter: IWebAnalyticsFilter) => void;
}): React.ReactElement {
  const restrictions = getFilterRestrictions(kind, filter);
  const update = (patch: IWebAnalyticsFilter): void => {
    onChange({ ...filter, ...patch });
  };

  return (
    <Space size={SPACING.lg} align="start" wrap>
      <RestrictedFilter reason={restrictions.provider}>
        <Select<EWebAnalyticsProvider>
          allowClear
          placeholder="Provider"
          style={{ width: 200 }}
          options={PROVIDER_OPTIONS}
          value={filter.provider}
          disabled={disabled || restrictions.provider !== undefined}
          onChange={(provider: EWebAnalyticsProvider | undefined): void => {
            update({ provider });
          }}
        />
      </RestrictedFilter>
      <RestrictedFilter reason={restrictions.searchType}>
        <Select<EWebAnalyticsSearchType>
          allowClear
          placeholder="Search type"
          style={{ width: 160 }}
          options={SEARCH_TYPE_OPTIONS}
          value={filter.searchType}
          disabled={disabled || restrictions.searchType !== undefined}
          onChange={(searchType: EWebAnalyticsSearchType | undefined): void => {
            update({ searchType });
          }}
        />
      </RestrictedFilter>
      <RestrictedFilter reason={restrictions.country}>
        <Input.Search
          allowClear
          placeholder="Country (e.g. svk)"
          style={{ width: 180 }}
          defaultValue={filter.country}
          disabled={disabled || restrictions.country !== undefined}
          onSearch={(value: string): void => {
            update({ country: value.trim() || undefined });
          }}
        />
      </RestrictedFilter>
      <RestrictedFilter reason={restrictions.device}>
        <Select<string>
          allowClear
          placeholder="Device"
          style={{ width: 140 }}
          options={DEVICE_OPTIONS}
          value={filter.device}
          disabled={disabled || restrictions.device !== undefined}
          onChange={(device: string | undefined): void => {
            update({ device });
          }}
        />
      </RestrictedFilter>
    </Space>
  );
}

export const queryPageColumns: ColumnType<IWebAnalyticsQueryPageRow>[] = [
  { title: 'Query', dataIndex: 'query', key: 'query' },
  { title: 'Page', dataIndex: 'page', key: 'page' },
  ...(['clicks', 'impressions', 'ctr', 'position'] as const).map(
    (metric): ColumnType<IWebAnalyticsQueryPageRow> => ({
      title: METRIC_TITLES[metric],
      dataIndex: metric,
      key: metric,
      align: 'right',
      render: (value: number): string => formatMetric(metric, value),
    }),
  ),
];

export const describeQueryPagesRequest = (
  request: IWebAnalyticsQueryPagesRequest | undefined,
): string =>
  request?.page ?? request?.query ?? request?.productUUID ?? 'Queries × pages';

export function QueryPagesDrawer({
  request,
  result,
  isLoading,
  onClose,
}: {
  request: IWebAnalyticsQueryPagesRequest | undefined;
  result: IWebAnalyticsQueryPagesResponse | undefined;
  isLoading: boolean;
  onClose: () => void;
}): React.ReactElement {
  return (
    <Drawer
      open={request !== undefined}
      size="large"
      title={describeQueryPagesRequest(request)}
      onClose={onClose}
    >
      <Space orientation="vertical" style={{ width: '100%' }}>
        {result?.periodStart ? (
          <Text type="secondary">
            Google Search Console, {result.periodStart} – {result.periodEnd}
          </Text>
        ) : null}
        <Table
          columns={queryPageColumns as ColumnType<object>[]}
          dataSource={(result?.rows ?? []).map(
            (row: IWebAnalyticsQueryPageRow) => ({
              ...row,
              key: `${row.query}\n${row.page}`,
            }),
          )}
          isLoading={isLoading}
          size="small"
          emptyText="No query × page data for this period"
        />
      </Space>
    </Drawer>
  );
}

function InsightsTable({
  settings,
  filter,
  table,
  isLoading,
  onChange,
  onDrillDown,
}: {
  settings: IWebAnalyticsTableSettings;
  filter: IWebAnalyticsFilter;
  table: IWebAnalyticsTableResponse | undefined;
  isLoading: boolean;
  onChange: (settings: IWebAnalyticsTableSettings) => void;
  onDrillDown: (request: IWebAnalyticsQueryPagesRequest) => void;
}): React.ReactElement {
  return (
    <Space orientation="vertical" size={SPACING.md} style={{ width: '100%' }}>
      <Space size={SPACING.lg} wrap>
        <Segmented<EWebAnalyticsTableKind>
          options={KIND_OPTIONS}
          value={settings.kind}
          disabled={isLoading}
          onChange={(kind: EWebAnalyticsTableKind): void => {
            onChange({
              ...settings,
              ...getDefaultSort(kind, filter.provider),
              kind,
              page: 1,
            });
          }}
        />
        <Input.Search
          allowClear
          placeholder="Search"
          style={{ width: 240 }}
          defaultValue={settings.search}
          disabled={isLoading}
          onSearch={(value: string): void => {
            onChange({ ...settings, search: value.trim(), page: 1 });
          }}
        />
      </Space>

      {table ? (
        <GapNote gaps={table.gaps} ignoredFilters={table.ignoredFilters} />
      ) : null}

      <Table
        columns={
          buildColumns({
            kind: settings.kind,
            provider: filter.provider,
            settings,
            onDrillDown,
          }) as ColumnType<object>[]
        }
        dataSource={table?.rows ?? []}
        isLoading={isLoading}
        emptyText="No data for this period"
        pagination={{
          current: settings.page,
          pageSize: settings.pageSize,
          total: table?.total ?? 0,
          onChange: (page: number, pageSize: number): void => {
            onChange({ ...settings, page, pageSize });
          },
        }}
        onChange={(
          _pagination: object,
          _filters: Record<string, FilterValue | null>,
          sorter: SorterResult<object> | SorterResult<object>[],
          extra: TableCurrentDataSource<object>,
        ): void => {
          if (extra.action !== 'sort') {
            return;
          }

          const sort = toSortSettings(sorter);

          onChange({
            ...settings,
            ...(sort ?? getDefaultSort(settings.kind, filter.provider)),
            page: 1,
          });
        }}
      />
    </Space>
  );
}

export default InsightsTable;
